/**
 * export-ceiling.js — потолок экспортного рендера (window.ExportCeiling)
 *
 * Класс дефекта: тул рендерит float (Web Audio не клиппирует; Windows-микшер
 * в превью ещё и мягко ограничивает >0 дБFS), а экспорт зажимает в int16 —
 * пользователь слышит «в браузере идеально, в файле треск». Первый случай —
 * /join (19.08.2026: громкость дорожки 200% + сумма кроссфейда, у
 * пользователя ~96 трещинок/с); второй ТЕМ ЖЕ ДНЁМ — /audio-scene (слои
 * сцены поверх громкого мастера: 24% сэмплов в потолке). Второй случай
 * класса = примитив, дальше копий не заводить.
 *
 * Третий случай (30.09.2026, /speed, чат Премиума 661) пришёл НЕ из тула, а из
 * движка растяжения: фазовый вокодер (Signalsmith) сохраняет спектр, но не
 * форму волны — пики громкого файла вырастают на 3–5 дБ (замер: речь в 0 дБFS
 * → +3.4 дБ, 119 клипов за 16 с; сжатая музыка → +4.9 дБ, 113 клипов в
 * секунду). Тул, который этот примитив не звал, клиппировал молча. Поэтому
 * правило теперь исполняет и сам движок, в своём воркере: `limitChannels` —
 * синхронное ядро без window, его импортирует signalsmith-worker.js.
 *
 * Одна формула на все тулы: peak-скан → ранний выход для неперегруженного
 * рендера (байт-в-байт как раньше, ноль стоимости для 99% работ) →
 * true-peak лимитер мастеринга с потолком −0.5 дБ (статическая
 * нормализация вниз ПРОИГРАЛА замером в /join: топит весь файл за перегруз
 * одной дорожки — непогруженный трек 0.966→0.531; лимитер давит только
 * окна перегруза). Телеметрия export_ceiling — только когда потолок
 * реально вмешался.
 *
 * Требует mastering-limiter.js (MasteringLimiter); без него — статический
 * спуск (честнее клипа). Сим для гардов: window.__exportCeilMode = 'off'
 * отключает потолок, чтобы тест мог доказать и дефект, и что потолок включён
 * по умолчанию.
 */
(function (root) {
    'use strict';
    if (root.ExportCeiling) return;

    var CEIL_DB = -0.5;
    var CEIL_LIN = Math.pow(10, CEIL_DB / 20);
    var TRIGGER = 0.999;

    function peakOf(chans) {
        var peak = 0, c, d, i, v;
        for (c = 0; c < chans.length; c++) {
            d = chans[c];
            for (i = 0; i < d.length; i++) { v = d[i] < 0 ? -d[i] : d[i]; if (v > peak) peak = v; }
        }
        return peak;
    }

    /* Ядро над массивами каналов — одно правило для страницы и воркера.
       Не перегружено → те же массивы, байт-в-байт (engaged:false).
       Перегружено → новые массивы под потолком CEIL_DB. */
    function staticDown(chans, peak) {
        var g = CEIL_LIN / peak;
        var out = chans.map(function (d) {
            var o = new Float32Array(d.length);
            for (var i = 0; i < d.length; i++) o[i] = d[i] * g;
            return o;
        });
        return { out: out, grDb: -20 * Math.log10(g) };
    }
    function verdict(peak, r) {
        return { out: r.out, engaged: true, peak: peak, overDb: 20 * Math.log10(peak), grDb: r.grDb };
    }
    function limitChannels(chans, sr) {
        var peak = peakOf(chans);
        if (!(peak > TRIGGER)) return { out: chans, engaged: false, peak: peak };
        var ML = root.MasteringLimiter;
        if (!(ML && typeof ML.limit === 'function')) return verdict(peak, staticDown(chans, peak));
        var r = ML.limit(chans, sr, { ceilingDb: CEIL_DB });
        return verdict(peak, { out: r.out, grDb: r.maxGrDb });
    }
    /* То же для длинного файла — НА МЕСТЕ и блоками. Две причины:
       · память: второй полноразмерный выход в воркере — это ещё 200 МБ на
         10 минутах стерео, на телефоне это смерть воркера; потоковый лимитер
         держит ~4 с и выдаёт ровно то, что limit() на всём файле
         (mastering-limiter.js, createStreamLimiter);
       · сторож воркера (30 с тишины = убит): лимитер длинного файла идёт
         десятки секунд, onProgress между блоками даёт повод подать голос.
       Запись назад в те же массивы безопасна: push() копирует вход во
       внутренний буфер, а выдача отстаёт от входа на блок. */
    async function limitChannelsAsync(chans, sr, onProgress) {
        var peak = peakOf(chans);
        if (!(peak > TRIGGER)) return { out: chans, engaged: false, peak: peak };
        var ML = root.MasteringLimiter;
        if (!(ML && typeof ML.createStreamLimiter === 'function')) return verdict(peak, staticDown(chans, peak));
        var N = chans[0].length, nCh = chans.length, STEP = 1 << 17, pos = 0, w = 0;
        var lim = ML.createStreamLimiter(sr, nCh, { ceilingDb: CEIL_DB });
        /* Уступать поток нужно только странице (воркер шлёт сердцебиение и
           без этого); на странице — через __yieldTick: голый setTimeout в
           скрытой вкладке тикает раз в секунду, потом раз в минуту. */
        var yieldTick = typeof root.document === 'undefined' ? null
            : (typeof root.__yieldTick === 'function' ? root.__yieldTick
                : function () { return new Promise(function (r) { setTimeout(r, 0); }); });
        function put(blocks) {
            for (var b = 0; b < blocks.length; b++) {
                for (var c = 0; c < nCh; c++) chans[c].set(blocks[b][c], w);
                w += blocks[b][0].length;
            }
        }
        while (pos < N) {
            var e = Math.min(N, pos + STEP);
            put(lim.push(chans.map(function (d) { return d.subarray(pos, e); })));
            pos = e;
            if (onProgress) { try { onProgress(pos / N); } catch (_) {} }
            if (yieldTick) await yieldTick(0);
        }
        var tail = lim.flush();
        put(tail.blocks);
        return verdict(peak, { out: chans, grDb: tail.maxGrDb });
    }

    async function apply(rendered, opts) {
        opts = opts || {};
        var mode = root.__exportCeilMode || 'limit';
        if (mode === 'off') return { buffer: rendered, engaged: false };
        var c, chans = [];
        for (c = 0; c < rendered.numberOfChannels; c++) chans.push(rendered.getChannelData(c));
        var peak = peakOf(chans);
        if (peak <= TRIGGER) return { buffer: rendered, engaged: false };
        if (root.MasteringLimiter) {
            var r = await root.MasteringLimiter.limitAsync(chans, rendered.sampleRate, { ceilingDb: CEIL_DB });
            for (c = 0; c < rendered.numberOfChannels; c++) rendered.getChannelData(c).set(r.out[c]);
        } else {
            var g = CEIL_LIN / peak;
            for (c = 0; c < chans.length; c++) {
                var d = chans[c];
                for (var i = 0; i < d.length; i++) d[i] *= g;
            }
        }
        var overDb = 20 * Math.log10(peak);
        try {
            root.__ae && root.__ae('feature_use', {
                slug: opts.slug || 'unknown', f: 'export_ceiling',
                over_db: Math.round(overDb * 10) / 10, mode: 'limit'
            });
        } catch (_) {}
        return { buffer: rendered, engaged: true, overDb: overDb };
    }

    /* Потоковая дверь — для рендера ОКНАМИ (2026-10-10, /audio-mixer, чат 679:
       25-минутный микс на iPhone падал на втором полноразмерном буфере). Окна
       идут в кодировщик по мере готовности, и весь файл ни разу не лежит в
       памяти — а apply() выше хочет его целиком.

       Правило то же, что у apply(): рендер без сэмпла выше TRIGGER выходит
       байт-в-байт и ничего не стоит (лимитер мастеринга — 9 с на 25 минутах
       стерео даже там, где ему нечего делать, замер 10.10). Первый сэмпл выше
       TRIGGER включает потоковый лимитер — до конца файла, как apply()
       пропускает через него весь файл. Чтобы лимитеру было на что опереться
       слева, последние HOLD секунд (1 с ≫ его радиус влияния 40–80 мс) не
       отдаются, пока не прочитан следующий кусок: включившись, он получает их
       первыми. Вдали от перегруза limit() возвращает вход как есть, поэтому
       стык «отдано без лимитера | отдано лимитером» — без шва.

       Чем отличается от apply() на целом файле: пики true-peak между потолком
       и TRIGGER ДО первого перегруза не трогаются (apply() их срезал бы, раз
       файл перегружен где-то дальше). На int16 это не влияет — клипа там нет.

       push(chans) → [блоки]; flush() → { blocks, engaged, peak, overDb, grDb }.
       Каждый отданный массив — свой (его можно передать в воркер с transfer). */
    function createStream(sr, nCh, opts) {
        opts = opts || {};
        var off = (root.__exportCeilMode || 'limit') === 'off';
        var HOLD = Math.max(1, Math.round((opts.holdSec > 0 ? opts.holdSec : 1) * sr));
        var held = [], heldLen = 0;          // ещё не отданные куски (свои копии)
        var lim = null, peak = 0, noLimiter = false;

        function copy(chans, from, to) {
            var out = [];
            for (var c = 0; c < nCh; c++) out.push(chans[c].slice(from, to));
            return out;
        }
        /* Отдать всё удержанное, кроме последних keep отсчётов. */
        function release(keep) {
            var out = [], give = heldLen - keep;
            while (give > 0 && held.length) {
                var h = held[0], n = h[0].length;
                if (n <= give) { out.push(h); held.shift(); heldLen -= n; give -= n; continue; }
                out.push(copy(h, 0, give));
                held[0] = copy(h, give, n);
                heldLen -= give; give = 0;
            }
            return out;
        }
        function engage() {
            var ML = root.MasteringLimiter;
            if (!(ML && typeof ML.createStreamLimiter === 'function')) {
                noLimiter = true;
                try { root.__ae && root.__ae('feature_use', { slug: opts.slug || 'unknown', f: 'export_ceiling_unavailable' }); } catch (_) {}
                return release(HOLD);
            }
            lim = ML.createStreamLimiter(sr, nCh, { ceilingDb: CEIL_DB });
            var out = [];
            for (var i = 0; i < held.length; i++) out = out.concat(lim.push(held[i]));
            held = []; heldLen = 0;
            return out;
        }
        return {
            push: function (chans) {
                var n = chans && chans[0] ? chans[0].length : 0;
                if (!n) return [];
                if (lim) return lim.push(chans);
                var p = peakOf(chans);
                if (p > peak) peak = p;
                held.push(copy(chans, 0, n)); heldLen += n;
                if (!off && !noLimiter && p > TRIGGER) return engage();
                return release(HOLD);
            },
            flush: function () {
                if (!lim) return { blocks: release(0), engaged: false, peak: peak };
                var tail = lim.flush();
                var overDb = 20 * Math.log10(peak);
                try {
                    root.__ae && root.__ae('feature_use', {
                        slug: opts.slug || 'unknown', f: 'export_ceiling',
                        over_db: Math.round(overDb * 10) / 10, mode: 'limit_stream'
                    });
                } catch (_) {}
                return { blocks: tail.blocks, engaged: true, peak: peak, overDb: overDb, grDb: tail.maxGrDb };
            }
        };
    }

    var API = { apply: apply, createStream: createStream, limitChannels: limitChannels, limitChannelsAsync: limitChannelsAsync, peakOf: peakOf, CEIL_DB: CEIL_DB };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    root.ExportCeiling = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
